import { useEffect, useRef } from 'react'
import { cn } from '~/@/lib/utils'
import { NotificationType } from '~/common/generated/apollo-operations'
import { useNotifications } from '~/common/hooks/useNotifications'
import { LoadingSpinner } from '../ui/atoms/loading-spinner/LoadingSpinner'
import { ActionStepNotification } from './ActionStepNotification'
import { CommentNotification } from './CommentNotification'
import { NotificationEmptyState } from './NotificationEmptyState'
import { NotificationHeader } from './NotificationHeader'

type NotificationPopoverContentProps = {
  onClose: () => void
}

export function NotificationPopoverContent({ onClose }: NotificationPopoverContentProps) {
  const ref = useRef<HTMLDivElement>(null)
  const { notifications, handleScroll, loading } = useNotifications()

  useEffect(() => {
    const scrollRef = ref.current
    const scrollHandler = () => {
      if (!handleScroll) {
        return
      }
      if (scrollRef) {
        handleScroll(scrollRef)
      }
    }
    scrollRef?.addEventListener('scroll', scrollHandler)

    return () => {
      scrollRef?.removeEventListener('scroll', scrollHandler)
    }
  }, [handleScroll])

  return (
    <div className="relative flex size-full flex-col overflow-hidden">
      <div className="w-full">
        <NotificationHeader onClose={onClose} />
      </div>
      <div
        className={cn(
          'hidden absolute w-full h-full items-center justify-center bg-white bg-opacity-50 z-[1301]',
          loading && 'flex',
        )}
      >
        <LoadingSpinner type="auth" />
      </div>
      <div ref={ref} className="relative flex size-full flex-1 flex-col overflow-y-auto">
        {notifications.length == 0 ? (
          <NotificationEmptyState />
        ) : (
          notifications.map((notification) => {
            // Both render through the same card: it takes a title, a message, a severity colour and
            // an optional link, which is what either event has to say. Only its name is specific.
            if (
              notification.notificationType === NotificationType.ACTION_STEP_NOTIFICATION ||
              notification.notificationType === NotificationType.WORKFLOW_V2_INVALIDATED
            ) {
              return <ActionStepNotification key={notification.id} notification={notification} />
            }
            return <CommentNotification key={notification.id} notification={notification} />
          })
        )}
      </div>
    </div>
  )
}
